/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { api, type ApiResult } from "./client";
import type { QueueEntry } from "./queue";

/** Mirror of nexus AgentRun (camelCase wire format). */
export type AgentRun = {
  id: string;
  agentType: string;
  trigger: string;
  providerId: string | null;
  freedSlotStartsAt: string | null;
  freedSlotEndsAt: string | null;
  status: string;
  triggerPayload: unknown;
  resultSummary: ResultSummary | null;
  startedAt: string;
  completedAt: string | null;
  sourceEventId: string | null;
};

/** The decision summary the agent writes when it finishes deciding. */
export type ResultSummary = {
  outcome?: string;
  seededCount?: number;
  providerId?: string;
  freedSlotStartsAt?: string;
  freedSlotEndsAt?: string;
  shortlist?: {
    rank: number;
    score: number;
    acuity?: string;
    patientId: string;
    source?: "TRANSITION" | "WAITLIST";
  }[];
  exclusions?: { reason: string; detail?: string; patientId: string }[];
};

export type OutreachStep = {
  outreach: {
    id: string;
    rankPosition: number;
    score: number | null;
    status: string;
    outcome: string | null;
    skipReason: string | null;
    smsSentAt: string | null;
    reminderSentAt: string | null;
    responseAt: string | null;
    response: string | null;
    booked: boolean;
    bookedHealthieAppointmentId: string | null;
    patientId: string;
  };
  patientFirstName: string | null;
  patientLastName: string | null;
  patientExternalId: string | null;
  patientFamilyId: number | null;
  responseBody: string | null;
};

/** A payer id with its registry display name (the raw id when unregistered). */
export type PayerRef = { id: string; displayName: string };

/** The provider whose freed slot triggered the run. */
export type RunProvider = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  healthieId: string | null;
  perStatePayerTags: boolean;
  licensedStates: string[];
};

/** One excluded candidate, named server-side; context fields are null on pre-context runs. */
export type RunExclusion = {
  patientId: string | null;
  familyId: number | null;
  patientFirstName: string | null;
  patientLastName: string | null;
  patientHealthieId: string | null;
  source: string | null;
  state: string | null;
  reason: string | null;
  detail: string | null;
  payer: PayerRef | null;
  providerAccepts: PayerRef[] | null;
};

export type RunDetail = {
  run: AgentRun;
  provider: RunProvider | null;
  outreach: OutreachStep[];
  escalations: QueueEntry[];
  exclusions: RunExclusion[];
};

export function listRuns(limit = 50): Promise<ApiResult<AgentRun[]>> {
  return api<AgentRun[]>(`/v1/agent-runs?limit=${limit}`);
}

export function runDetail(id: string): Promise<ApiResult<RunDetail>> {
  return api<RunDetail>(`/v1/agent-runs/${id}`);
}
